import type {
    CaseDto,
    PatientDetailDto,
    SeriesDto,
} from '~/backend/connector-next-api/connector-next-schemas';
import {
    flattenSeries,
    imageContouredOn,
    isImageSeries,
} from './patientListModel';

/**
 * What an open or a panel view is narrowed to: one of the patient's cases, or
 * datasets the user ticked. A selection carries the case it was ticked in, if
 * any, because that case's primaries are the ones a selection keeps.
 */
export type CaseScope =
    | { caseId: string }
    | { datasetIds: readonly string[]; fromCaseId?: string };

/** A primary image and structure set, null when nobody has chosen one. */
export type Primaries = {
    imageId: string | null;
    structureSetId: string | null;
};

/** Whether a series can be a member of a case: an image series or a structure set with an id. */
export function isCaseMemberKind(series: SeriesDto): boolean {
    return (
        !!series.id &&
        (isImageSeries(series) || series.datasetType === 'StructureSet')
    );
}

/** The patient's cases, archived or not, in the order the Connector lists them. */
export function casesOf(
    detail: PatientDetailDto | undefined,
    { archived }: { archived: boolean }
): CaseDto[] {
    return (detail?.cases ?? []).filter(
        (c) => !!c.id && (c.isArchived ?? false) === archived
    );
}

/**
 * The name a new case starts with: "Case" and the number after the patient's
 * case count, moved on past any name already taken, compared as the Connector
 * compares names, without regard to case.
 */
export function defaultCaseName(cases: readonly CaseDto[]): string {
    const taken = new Set(cases.map((c) => c.name?.toLocaleLowerCase()));
    let n = cases.length + 1;
    while (taken.has(`case${n}`)) {
        n += 1;
    }
    return `Case${n}`;
}

/**
 * The ids followed by the ids of the images their structure sets are drawn on,
 * where the patient holds them — the same closure the Connector applies when
 * a set joins a case, so what the user is shown is what the case will hold.
 */
export function withImagesOfStructureSets(
    series: readonly SeriesDto[],
    ids: readonly string[]
): string[] {
    const result = [...ids];
    const images = series.filter(isImageSeries);
    for (const id of ids) {
        const set = series.find(
            (s) => s.id === id && s.datasetType === 'StructureSet'
        );
        const image = set ? imageContouredOn(images, set) : undefined;
        if (image?.id && !result.includes(image.id)) {
            result.push(image.id);
        }
    }
    return result;
}

/**
 * The case's structure sets that leave it along with `removedIds` without
 * being among them: those drawn on an image being removed, which cannot be
 * worked on without it — the same rule the Connector applies to a removal.
 */
export function setsLeavingWithImages(
    detail: PatientDetailDto,
    caseId: string,
    removedIds: readonly string[]
): SeriesDto[] {
    const members = new Set(
        detail.cases?.find((c) => c.id === caseId)?.memberIds ?? []
    );
    const series = flattenSeries(detail);
    const images = series.filter(isImageSeries);
    return series.filter((s) => {
        if (
            s.datasetType !== 'StructureSet' ||
            !s.id ||
            !members.has(s.id) ||
            removedIds.includes(s.id)
        ) {
            return false;
        }
        const image = imageContouredOn(images, s);
        return image?.id !== undefined && removedIds.includes(image.id);
    });
}

/**
 * The designations in view: the case's when one is named, else the patient's.
 * The patient holds none once it has a case, so with cases and no case named
 * there is nothing to show.
 */
export function primariesInView(
    detail: PatientDetailDto | undefined,
    caseId: string | undefined
): Primaries {
    const holder =
        caseId === undefined
            ? detail
            : detail?.cases?.find((c) => c.id === caseId);
    return {
        imageId: holder?.primaryImageId ?? null,
        structureSetId: holder?.primaryStructureSetId ?? null,
    };
}

/**
 * The detail narrowed to a scope: only its images and structure sets, the
 * registrations between its images, and its designations as the primaries.
 * Plans and doses are left out; a case is what the viewer loads, and they
 * open only with their image. A selection keeps the designations of the view
 * it was ticked in only where they are among its objects.
 *
 * Undefined when the scope names a case the patient does not have: the open
 * is refused rather than widened to the whole patient.
 */
export function scopeDetail(
    detail: PatientDetailDto,
    scope: CaseScope
): PatientDetailDto | undefined {
    const series = flattenSeries(detail);
    let members: Set<string>;
    let primaries: Primaries;
    if ('caseId' in scope) {
        const target = detail.cases?.find((c) => c.id === scope.caseId);
        if (target === undefined) {
            return undefined;
        }
        members = new Set(target.memberIds ?? []);
        primaries = primariesInView(detail, scope.caseId);
    } else {
        members = new Set(withImagesOfStructureSets(series, scope.datasetIds));
        const inView = primariesInView(detail, scope.fromCaseId);
        primaries = {
            imageId:
                inView.imageId && members.has(inView.imageId)
                    ? inView.imageId
                    : null,
            structureSetId:
                inView.structureSetId && members.has(inView.structureSetId)
                    ? inView.structureSetId
                    : null,
        };
    }

    const frames = new Set(
        series
            .filter((s) => s.id && members.has(s.id) && isImageSeries(s))
            .map((s) => s.frameOfReferenceUid)
            .filter((frame): frame is string => !!frame)
    );
    const inScope = (s: SeriesDto): boolean =>
        s.datasetType === 'Registration'
            ? !!s.sourceFrameOfReferenceUid &&
              !!s.targetFrameOfReferenceUid &&
              frames.has(s.sourceFrameOfReferenceUid) &&
              frames.has(s.targetFrameOfReferenceUid)
            : !!s.id && members.has(s.id);

    return {
        ...detail,
        studies: (detail.studies ?? [])
            .map((study) => ({
                ...study,
                series: (study.series ?? []).filter(inScope),
            }))
            .filter((study) => study.series.length > 0),
        primaryImageId: primaries.imageId,
        primaryStructureSetId: primaries.structureSetId,
    };
}

/** Why a scope cannot be opened. */
export type ScopeRefusal =
    | 'caseIsGone'
    | 'selectionIsGone'
    | 'caseHasNoImage'
    | 'selectionHasNoImage';

/**
 * What an open of `scope` loads, with how many of a selection's datasets the
 * patient no longer holds; or why the scope cannot be opened: its case has
 * gone, none of the selection is left, or nothing in it is an image to
 * display. Without a scope the whole patient opens.
 */
export function scopeToOpen(
    detail: PatientDetailDto,
    scope: CaseScope | undefined
): { detail: PatientDetailDto; goneCount: number } | { refusal: ScopeRefusal } {
    if (scope === undefined) {
        return { detail, goneCount: 0 };
    }
    const narrowed = scopeDetail(detail, scope);
    if (narrowed === undefined) {
        return { refusal: 'caseIsGone' };
    }
    let goneCount = 0;
    if ('datasetIds' in scope) {
        const held = new Set(flattenSeries(detail).map((s) => s.id));
        goneCount = scope.datasetIds.filter((id) => !held.has(id)).length;
        if (goneCount === scope.datasetIds.length) {
            return { refusal: 'selectionIsGone' };
        }
    }
    if (!flattenSeries(narrowed).some(isImageSeries)) {
        return {
            refusal:
                'caseId' in scope ? 'caseHasNoImage' : 'selectionHasNoImage',
        };
    }
    return { detail: narrowed, goneCount };
}

/**
 * Whether the case still holds an image once `removedIds` leave it — the
 * Connector refuses a removal that would leave it none.
 */
export function caseKeepsAnImage(
    detail: PatientDetailDto,
    caseId: string,
    removedIds: readonly string[]
): boolean {
    const members = new Set(
        detail.cases?.find((c) => c.id === caseId)?.memberIds ?? []
    );
    return flattenSeries(detail).some(
        (s) =>
            isImageSeries(s) &&
            !!s.id &&
            members.has(s.id) &&
            !removedIds.includes(s.id)
    );
}

/**
 * The patient designation that making its first case of these datasets would
 * drop, or undefined when none would be. The first case takes over each of
 * the patient's designations that is among its members: without the primary
 * image both designations go, and with the image but not the primary
 * structure set, the set's designation goes. The user is told before either
 * happens.
 */
export function primaryLeftOutOfFirstCase(
    detail: PatientDetailDto | undefined,
    datasetIds: readonly string[]
): { kind: 'image' | 'structureSet'; datasetId: string } | undefined {
    if (!detail || (detail.cases?.length ?? 0) > 0) {
        return undefined;
    }
    const members = withImagesOfStructureSets(
        flattenSeries(detail),
        datasetIds
    );
    const { primaryImageId, primaryStructureSetId } = detail;
    if (primaryImageId && !members.includes(primaryImageId)) {
        return { kind: 'image', datasetId: primaryImageId };
    }
    if (primaryStructureSetId && !members.includes(primaryStructureSetId)) {
        return { kind: 'structureSet', datasetId: primaryStructureSetId };
    }
    return undefined;
}
